import React, { useEffect, useState } from "react";
import TableContainer from "Common/TableContainer";
import {
  addLoanPayment,
  getLoanPaymentList,
  getLoanPaymentStatusList,
  getLoanPaymentReceiptPDF,
  getLoanPaymentByCodeAndLoanId,
  getLoanPaymentReportXLS,
  getLoanList,
} from "../../helpers/fakebackend_helper";
import { Plus } from 'lucide-react';

import Select from 'react-select';
import Flatpickr from 'react-flatpickr';
import { Search } from 'lucide-react';
import moment from "moment";
import { ToastContainer } from 'react-toastify';
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useLoader } from "../../context/LoaderContext";
import Modal from 'Common/Components/Modal';
import CurrencyGTCell from "Common/Components/CurrencyGTCell";

interface LoanPaymentInterface {
  loanPaymentId: string;
  name: string;
  loan: any;
  loanPaymentState: any;
}

const LoanPaymentList = () => {
  const FlatpickrComponent = Flatpickr as unknown as React.ComponentType<any>;
  const defaultStateId = 1;
  const defaultStartDate = moment().startOf("month").toDate();
  const defaultEndDate = moment().endOf("month").toDate();
  const [selectedStateId, setSelectedStateId] = useState<number | null>(defaultStateId);
  const [dateRange, setDateRange] = useState<any[]>([defaultStartDate, defaultEndDate]);
  const [loanPaymentList, setLoanPaymentList] = useState([]);
  const { showLoader, hideLoader } = useLoader();

  const [loanPaymentOptions, setLoanPaymentListByCode] = useState<{
    value: number;
    label: string;
    shareNumber: number;
    capitalBalance: number;
    amountDate: string;
    quota: number;
    interest: number;
    defaultInterest?: number;
    capital: number;
    totalRemaining: number;
    others: number;
    amountPaid?: number;
  }[]>([]);


  const [loanPaymentStateOptions, setLoanPaymentState] = useState<{
    value: number; label: string;
  }[]
  >([]);

  const [loanOptions, setLoanOptions] = useState<{ value: number; label: string }[]>([]);

  const [show, setShow] = useState(false);
  const [eventData, setEventData] = useState<LoanPaymentInterface | null>(null);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      loan: 0,
      loanPayment: 0,
      shareNumber: 0,
      quota: 0,
      interest: 0,
      amountPaid: 0,
      defaultInterest: 0,
      capitalBalance: 0,
      capital: 0,
      totalRemaining: 0,
      others: 0,
      paymentDate: moment().format("YYYY-MM-DD"),
      comments: "",
    },
    validationSchema: Yup.object({
      loan: Yup.string().required("Número de préstamo es requerido"),
      loanPayment: Yup.string().required("Cuota de pago es requerido"),
      amountPaid: Yup.number()
        .required("Monto a pagar es requerido")
        .min(0, "El monto debe ser mayor o igual a 0"),
    }),

    onSubmit: async (values) => {

      showLoader();
      try {
        const payload = {
          loanId: values.loan,
          loanPaymentId: values.loanPayment,
          paymentDate: values.paymentDate,
          amountPaid: values.amountPaid,
          comments: values.comments
        };

        await addLoanPayment(payload);
        toast.success("Pago registrado exitosamente", { autoClose: 2000 });
        handleCloseModal();
        fetchPaymentList();
      } catch (error) {
        toast.error("Error al registrar el pago del préstamo: " + error, { autoClose: 2000 });
      } finally{
        hideLoader();
      }
    }
  });

  useEffect(() => {
    const fetchLoans = async () => {
      try {
        const params = {
          page: 0,
          size: 1000,
          sort: "loanId,desc"
        };

        const response = await getLoanList(params);
        const embedded = response.data?._embedded?.loans;
        const options = embedded
          ? Object.values(embedded).map((loans: any) => ({
            value: loans.loanId,
            label: loans.loanNumber + " - " + loans.loanApplication.customer.fullName + " - Q" + loans.principalAmount.toFixed(2),
          }))
          : [];
        setLoanOptions(options);
      } catch (error) {
        toast.error("Error fetching loans: " + error, { autoClose: 2000 });
      }
    };
    fetchLoans();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getLoanPaymentStatusList();
        const embedded = response.data?._embedded?.loanPaymentStates;
        const options = embedded
          ? Object.values(embedded).map((loanPaymentState: any) => ({
            value: loanPaymentState.loanPaymentStateId,
            label: loanPaymentState.name
          }))
          : [];
        setLoanPaymentState(options);
      } catch (error) {
        toast.error("Error fetching loan payment states: " + error, { autoClose: 2000 });
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!formik.values.loan) return;

      showLoader();

      try {
        const params = {
          loanId: formik.values.loan,
          codes: ["ATR", "VIG"],
        };

        const response = await getLoanPaymentByCodeAndLoanId(params);
        const loanPaymentsByCode = response.data?._embedded?.loanPayments || [];

        const options = loanPaymentsByCode.map((lp: any) => ({
          value: lp.loanPaymentId,
          label: `Cuota #${lp.shareNumber}  |  Q.${lp.quota}  |  ${lp.loanPaymentState.name}`,
          shareNumber: lp.shareNumber,
          capitalBalance: lp.capitalBalance,
          amountDate: lp.amountDate,
          quota: lp.quota,
          capital: lp.capital || 0,
          defaultInterest: lp.defaultInterest || 0,
          others: lp.others || 0,
          totalRemaining: lp.totalRemaining || 0,
          interest: lp.interest || 0,
        }));

        setLoanPaymentListByCode(options);
      } catch (error) {
        toast.error("Error fetching loan payments: " + error, { autoClose: 2000 });
      } finally{
        hideLoader();
      }
    };

    fetchData();
  }, [formik.values.loan]);

  const fetchPaymentList = async () => {
    showLoader();

    try {
      const params: any = {};
      if (selectedStateId) params.loanPaymentStateId = selectedStateId;
      if (dateRange.length === 2 && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].toISOString().split('T')[0];
        params.endDate = dateRange[1].toISOString().split('T')[0];
      }

      const response = await getLoanPaymentList(params);
      console.log("Loan Payment List Response:", response.data);
      const loanPaymentList = response.data || [];
      setLoanPaymentList(loanPaymentList);
    } catch (error) {
      toast.error("Error fetching loan payment list: " + error, { autoClose: 2000 });
    } finally {
      hideLoader();
    }
  };

  const handleDownloadLoanPaymentList = async () => {
    showLoader();

    try {
      const params: any = {};
      if (selectedStateId) params.loanPaymentStateId = selectedStateId;
      if (dateRange.length === 2 && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].toISOString().split('T')[0];
        params.endDate = dateRange[1].toISOString().split('T')[0];
        params.includePartners = false;
      }
      const response = await getLoanPaymentReportXLS(params);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Cuotas de Prestamos del '+ moment(params.startDate).format('YYYY-MM-DD') + ' al ' + moment(params.endDate).format('YYYY-MM-DD') + '.xlsx';
      a.click();
    } catch (error) {
      toast.error("Error downloading loan payment list: " + error, { autoClose: 2000 });
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchPaymentList();
  }, [selectedStateId, dateRange]);

  const handleCloseModal = () => {
    setShow(false);
    setEventData(null);
    formik.resetForm();
  };

  const handleCreateClick = () => {
    setEventData(null);
    formik.resetForm();
    setShow(true);
  };

  const StatusBadge = ({ code }: { code: string }) => {

    const getBadgeProps = (code: string) => {
      switch (code) {
        case "VIG":
          return {
            label: "Vigente",
            bg: "bg-blue-100",
            text: "text-blue-600",
            dark: "dark:bg-blue-500/20 dark:text-blue-400",
          };
        case "PAG":
          return {
            label: "Pagado",
            bg: "bg-green-100",
            text: "text-green-600",
            dark: "dark:bg-green-500/20 dark:text-green-400",
          };
        case "ATR":
          return {
            label: "Atrasado",
            bg: "bg-red-100",
            text: "text-red-600",
            dark: "dark:bg-red-500/20 dark:text-red-400",
          };
        case "PARCIAL":
          return {
            label: "Pago parcial",
            bg: "bg-yellow-100",
            text: "text-yellow-600",
            dark: "dark:bg-yellow-500/20 dark:text-yellow-400",
          };
        case "NA":
        default:
          return {
            label: "No Aplica",
            bg: "bg-gray-100",
            text: "text-gray-600",
            dark: "dark:bg-gray-500/20 dark:text-gray-400",
          };
      }
    };

    const { label, bg, text, dark } = getBadgeProps(code);

    return (
      <span
        className={`px-2.5 py-0.5 inline-block text-xs font-medium rounded border border-transparent ${bg} ${text} ${dark}`}
      >
        {label}
      </span>
    );
  };

  const downloadLoanPaymentReceipt = async (loanPaymentId: string) => {
    showLoader();

    try {
      const response = await getLoanPaymentReceiptPDF(loanPaymentId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `loan_payment_receipt_${loanPaymentId}.pdf`;
      a.click();
    } catch (error) {
      toast.error("Error al descargar comprobante de pago: " + error, { autoClose: 2000 });
    } finally {
      hideLoader();
    }
  };

  const columns = [
    {
      header: "No. préstamo",
      accessorKey: "loanNumber",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Cliente",
      accessorKey: "fullName",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "No. Cuota",
      accessorKey: "shareNumber",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Fecha de Pago",
      accessorKey: "amountDate",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Cliente",
      accessorKey: "amountDate",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Capital",
      accessorKey: "capital",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ getValue }: any) => <CurrencyGTCell value={getValue()} />,
    },
    {
      header: "Interes",
      accessorKey: "interest",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ getValue }: any) => <CurrencyGTCell value={getValue()} />,
    },
    {
      header: "Interes Moratorio",
      accessorKey: "defaultInterest",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ getValue }: any) => <CurrencyGTCell value={getValue()} />,
    },
    {
      header: "Saldo Pendiente",
      accessorKey: "totalRemaining",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ getValue }: any) => <CurrencyGTCell value={getValue()} />,
    },
    {
      header: "Monto Total",
      accessorKey: "quota",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ getValue }: any) => <CurrencyGTCell value={getValue()} />,
    },
    {
      header: "Estado",
      accessorKey: "code",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (cell: any) => {
        const statusCode = cell.getValue() || "NA";
        return <StatusBadge code={statusCode} />;
      },
    },
    {
      header: "Acciones",
      accessorKey: "actions",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (cell: any) => {
        const row = cell.row.original;
        const code = row.code || "NA";
        if (code !== "PAG") {
          return null;
        }

        return (
          <div className="flex gap-3">
            <button
              onClick={() => downloadLoanPaymentReceipt(row.loanPaymentId)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-red-100 text-red-600 hover:bg-red-500 hover:text-white transition-all text-sm font-medium shadow-sm"
              title="Descargar PDF"
            >
              <i className="ri-file-pdf-line text-base"></i>
              Pago
            </button>
          </div>
        );
      },
    }
  ];


  return (
    <div className="card">
      <ToastContainer closeButton={false} limit={1} />

      <div className="card-body">
        <div className="flex justify-end mb-4">
          <button onClick={handleCreateClick} className="btn bg-custom-500 text-white">
            <Plus className="inline size-4 mr-1" /> Registrar Pago
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-2 xl:grid-cols-12">

          <div className="xl:col-span-3">
            <div className="relative">
              <input type="text" className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Search for ..." autoComplete="off" />
              <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
            </div>
          </div>

          <div className="xl:col-span-2">
            <Select
              className="border-slate-200 focus:outline-none focus:border-custom-500"
              options={loanPaymentStateOptions}
              isSearchable={true}
              name="statusFilterSelect"
              id="statusFilterSelect"
              value={loanPaymentStateOptions.find(opt => opt.value === selectedStateId)}
              onChange={(selected) => {
                setSelectedStateId(selected?.value || null);
              }}
            />
          </div>

          <div className="xl:col-span-3">
            <FlatpickrComponent
              id="dateRangeFilterInput"
              className="form-input border-slate-200 dark:border-zink-500 ..."
              options={{ dateFormat: "d M, Y", mode: "range" }}
              value={dateRange}
              onChange={(range: Date[]) => {
                if (range.length === 2 && range[0] instanceof Date && range[1] instanceof Date) {
                  setDateRange(range);
                }
              }}
              placeholder="Select date"
            />
          </div>

          <div className="xl:col-span-3">
            <button
              onClick={handleDownloadLoanPaymentList}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold rounded-lg shadow-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-300"
            >
              <i className="ri-file-excel-2-line text-lg"></i>
              Descargar Excel
            </button>
          </div>
        </div>

        <TableContainer
          isPagination={true}
          isSelect={true}
          isGlobalFilter={false}
          columns={columns}
          data={loanPaymentList}
          customPageSize={5}
          divclassName="my-2 col-span-12 overflow-x-auto lg:col-span-12"
          tableclassName="hover group dataTable w-full text-sm align-middle whitespace-nowrap no-footer"
          theadclassName="border-b border-slate-200 dark:border-zink-500"
          trclassName="group-[.stripe]:even:bg-slate-50 group-[.stripe]:dark:even:bg-zink-600 transition-all duration-150 ease-linear group-[.hover]:hover:bg-slate-50 dark:group-[.hover]:hover:bg-zink-600 [&.selected]:bg-custom-500 dark:[&.selected]:bg-custom-500 [&.selected]:text-custom-50 dark:[&.selected]:text-custom-50"
          thclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500 sorting px-3 py-4 text-slate-900 bg-slate-200/50 font-semibold text-left dark:text-zink-50 dark:bg-zink-600 dark:group-[.bordered]:border-zink-500"
          tdclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500"
          PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
        />
      </div>

      <Modal show={show} onHide={handleCloseModal} modal-center
        className="fixed inset-0 z-drawer flex items-center justify-center"
        dialogClassName="w-screen max-w-3xl bg-white shadow-xl rounded-xl dark:bg-zink-600"
      >
        <Modal.Header className="flex items-center justify-between px-6 py-4 border-b dark:border-zink-500">
          <Modal.Title className="text-lg font-semibold text-slate-900 dark:text-zink-100">
            {"Registrar Pago de Prestamo"}
          </Modal.Title>

          <button onClick={handleCloseModal} className="text-slate-400 hover:text-red-500 transition">
            ×
          </button>
        </Modal.Header>

        <Modal.Body className="p-6">
          <form onSubmit={formik.handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4">


            <div className="col-span-2">
              <label htmlFor="loan" className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100">
                Número de Préstamo <span className="text-red-500">*</span>
              </label>

              <Select
                name="loan"
                isSearchable={true}
                placeholder="Seleccionar préstamo..."
                value={loanOptions.find(opt => opt.value === formik.values.loan) || null}
                onChange={(selected) => {
                  formik.setFieldValue('loan', selected?.value || '');
                }}
                onBlur={formik.handleBlur}
                options={loanOptions}
                className="w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500"
              />
              {formik.touched.loan && formik.errors.loan && (
                <p className="text-sm text-red-500 mt-1">{formik.errors.loan}</p>
              )}
            </div>

            <div className="col-span-2">
              <label htmlFor="loanPayment" className="block mb-1 text-sm font-medium text-slate-700 dark:text-zink-100">
                Cuota de Pago <span className="text-red-500">*</span>
              </label>

              <Select
                name="loanPayment"
                isSearchable={true}
                placeholder="Seleccionar cuota de préstamo..."
                value={loanPaymentOptions.find(opt => opt.value === formik.values.loanPayment) || null}
                onChange={(selected) => {
                  formik.setFieldValue('loanPayment', selected?.value || '');
                  formik.setFieldValue('shareNumber', selected?.shareNumber || 0);
                  formik.setFieldValue('capitalBalance', selected?.capitalBalance || 0);
                  // formik.setFieldValue('paymentDate', selected?.amountDate || '');
                  formik.setFieldValue('quota', selected?.quota || 0);
                  formik.setFieldValue('capital', selected?.capital || 0);
                  formik.setFieldValue('defaultInterest', selected?.defaultInterest || 0);
                  formik.setFieldValue('totalRemaining', selected?.totalRemaining || 0);
                  formik.setFieldValue('others', selected?.others || 0);
                  formik.setFieldValue('interest', selected?.interest || 0);
                  formik.setFieldValue('amountPaid', selected?.totalRemaining || 0);
                }}
                onBlur={formik.handleBlur}
                options={loanPaymentOptions}
                className="w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500"
              />
              {formik.touched.loanPayment && formik.errors.loanPayment && (
                <p className="text-sm text-red-500 mt-1">{formik.errors.loanPayment}</p>
              )}
            </div>

            {/* Capital */}
            <div className="col-span-1">
              <label
                htmlFor="capital"
                className="inline-block mb-2 text-base font-medium"
              >
                Capital
              </label>
              <input
                type="number"
                name="capital"
                value={formik.values.capital}
                readOnly
                className="form-input w-full border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>

            {/* Interes */}
            <div className="col-span-1">
              <label
                htmlFor="interest"
                className="inline-block mb-2 text-base font-medium"
              >
                Interes
              </label>
              <input
                type="number"
                name="interest"
                value={formik.values.interest}
                readOnly
                className="form-input w-full border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>

            {/* Interes Moratorio */}
            <div className="col-span-1">
              <label
                htmlFor="defaultInterest"
                className="inline-block mb-2 text-base font-medium"
              >
                Mora
              </label>
              <input
                type="number"
                name="defaultInterest"
                value={formik.values.defaultInterest}
                readOnly
                className="form-input w-full border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>

            {/* Others */}
            <div className="col-span-1">
              <label
                htmlFor="others"
                className="inline-block mb-2 text-base font-medium"
              >
                Otros
              </label>
              <input
                type="number"
                name="others"
                value={formik.values.others}
                readOnly
                className="form-input w-full border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>

            {/* Saldo Pendiente */}
            <div className="col-span-1">
              <label
                htmlFor="totalRemaining"
                className="inline-block mb-2 text-base font-medium"
              >
                Saldo pendiente
              </label>
              <input
                type="number"
                name="totalRemaining"
                value={formik.values.totalRemaining}
                readOnly
                className="form-input w-full border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>

            {/* Cuota */}
            <div className="col-span-1">
              <label
                htmlFor="quota"
                className="inline-block mb-2 text-base font-medium"
              >
                Cuota
              </label>
              <input
                type="number"
                name="quota"
                value={formik.values.quota}
                readOnly
                className="form-input w-full border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>

              {/* Monto a pagar */}
            <div className="col-span-1">
              <label
                htmlFor="amountPaid"
                className="inline-block mb-2 text-base font-medium"
              >
                Monto a pagar <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="amountPaid"
                value={formik.values.amountPaid}
                className="form-input w-full border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />

              {formik.touched.amountPaid && formik.errors.amountPaid && (
                <p className="text-sm text-red-500 mt-1">{formik.errors.amountPaid}</p>
              )}
            </div>

            <div className="col-span-1">
              <label
                htmlFor="paymentDate"
                className="inline-block mb-2 text-base font-medium"
              >
                Fecha de registro <span className="text-red-500">*</span>
              </label>
              <FlatpickrComponent
                id="paymentDate"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                options={{
                  altInput: true,
                  altFormat: "d M, Y",
                  dateFormat: "Y-m-d"
                }}
                name="paymentDate"
                onChange={(date: any) =>
                  formik.setFieldValue(
                    "paymentDate",
                    moment(date[0]).format("YYYY-MM-DD")
                  )
                }
                value={formik.values.paymentDate || ""}
              />
            </div>

            <div className="md:col-span-4">
              <label
                htmlFor="comments"
                className="inline-block mb-2 text-base font-medium"
              >
                Descripción
              </label>
              <textarea
                name="comments"
                className="form-textarea w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500 focus:border-custom-500 dark:focus:ring-custom-800 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                value={formik.values.comments}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={handleCloseModal}
                className="btn bg-white text-red-500 border border-red-500 hover:bg-red-50 transition">
                Cancelar
              </button>
              <button
                type="submit"
                className="btn bg-custom-500 text-white hover:bg-custom-600 transition">
                {"Agregar"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

    </div>
  );
};

export default LoanPaymentList;
